import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Star as StarIcon, Heart, MessageCircle } from "lucide-react";
import { fadeUp, viewport } from "@/lib/motion";
import { ScribbleUnderline } from "@/components/site/Scribbles";
import BookCoverArt from "@/components/books/BookCoverArt";

const cx = (...c) => c.filter(Boolean).join(" ");

/* ---------------- Button ---------------- */
export const Button = ({ variant = "primary", children, className = "", icon = true, as = "button", ...props }) => {
  const base = "group inline-flex items-center justify-center gap-2 rounded-full font-semibold text-[15px] px-6 py-3 transition-all duration-300 ease-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 focus-visible:ring-offset-cream disabled:opacity-60";
  const variants = {
    primary: "bg-coral-fill text-[#FFF7F0] shadow-soft hover:-translate-y-0.5 hover:shadow-lift",
    secondary: "bg-card text-ink border border-line hover:-translate-y-0.5 hover:border-coral/50 hover:shadow-soft",
    ghost: "bg-transparent text-ink px-2 py-1 hover:text-coral",
  };
  const Comp = as;
  return (
    <Comp className={cx(base, variants[variant], className)} {...props}>
      {children}
      {icon && <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-soft group-hover:translate-x-1" />}
    </Comp>
  );
};

/* ---------------- Badge ---------------- */
const toneMap = {
  coral: "bg-coral/12 text-coral",
  sage: "bg-sage/45 text-[#4f6249] dark:bg-sage/20 dark:text-[#b3c7aa]",
  yellow: "bg-yellow/30 text-[#8a6a29] dark:bg-yellow/15 dark:text-[#e2bd78]",
  blue: "bg-blue/20 text-[#4d616c] dark:bg-blue/20 dark:text-[#aabecb]",
  cream: "bg-[#F1E9DC] text-ink dark:bg-white/5",
};
export const Badge = ({ tone = "coral", children, className = "", ...props }) => (
  <span className={cx("inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold tracking-wide", toneMap[tone], className)} {...props}>
    {children}
  </span>
);

/* ---------------- SectionHeader ---------------- */
export const SectionHeader = ({ eyebrow, title, sub, align = "left", underline = false, className = "" }) => (
  <div className={cx("max-w-2xl", align === "center" && "mx-auto text-center", className)}>
    {eyebrow && (
      <motion.span variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="u-eyebrow mb-4">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-coral" />{eyebrow}
      </motion.span>
    )}
    <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}
      className="relative mt-3 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-ink sm:text-5xl">
      {title}
      {underline && <ScribbleUnderline inView className="mt-1 block h-4 w-52 text-coral" />}
    </motion.h2>
    {sub && (
      <motion.p variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={viewport}
        className="mt-5 max-w-xl text-base leading-relaxed text-subtle md:text-lg">
        {sub}
      </motion.p>
    )}
  </div>
);

/* ---------------- Stars ---------------- */
export const Stars = ({ value = 5, size = 14 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <StarIcon key={n} style={{ width: size, height: size }}
        className={n <= value ? "fill-yellow text-yellow" : "fill-none text-line"} />
    ))}
  </span>
);

/* ---------------- BookCard (real cover, title/author below) ---------------- */
export const BookCard = ({ title, author, rating, tone = "coral", className = "" }) => (
  <motion.div whileHover={{ y: -8, rotate: -1.5 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className={cx("group relative w-full", className)} data-testid="book-card">
    <BookCoverArt book={{ id: title, title, author, tone }} className="aspect-[2/3] w-full shadow-card" />
    <div className="mt-2 px-0.5">
      <p className="line-clamp-1 font-display text-sm font-semibold leading-tight text-ink text-balance">{title}</p>
      <p className="mt-0.5 truncate text-xs text-subtle">{author}</p>
      {rating && <div className="mt-1"><Stars value={Math.round(rating)} size={11} /></div>}
    </div>
  </motion.div>
);

/* ---------------- ReadingProgress ---------------- */
export const ReadingProgress = ({ value = 60, label }) => (
  <div className="w-full" data-testid="reading-progress">
    {label && <div className="mb-1.5 flex items-center justify-between text-xs font-medium text-subtle"><span>{label}</span><span className="text-coral">{value}%</span></div>}
    <div className="h-2 w-full overflow-hidden rounded-full bg-line/70">
      <motion.div initial={{ width: 0 }} whileInView={{ width: `${value}%` }} viewport={{ once: true }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="h-full rounded-full bg-gradient-to-r from-coral to-yellow" />
    </div>
  </div>
);

/* ---------------- ReaderActivityCard ---------------- */
const avatarTone = {
  coral: "from-coral to-[#b85e58]",
  yellow: "from-yellow to-[#e0b164]",
  sage: "from-sage to-[#a9c39d]",
  blue: "from-blue to-[#6b808b]",
};
export const ReaderActivityCard = ({ reader, className = "" }) => (
  <div className={cx("card-paper relative flex flex-col gap-3 p-5", className)} data-testid="reader-activity-card">
    <div className="flex items-center gap-3">
      <span className={cx("grid h-10 w-10 place-items-center rounded-full font-display text-base font-bold text-[#FFF7F0]", `bg-gradient-to-br ${avatarTone[reader.tone]}`)}>
        {reader.handle[0]}
      </span>
      <div className="leading-tight">
        <p className="text-sm font-semibold text-ink">@{reader.name}</p>
        <p className="text-xs text-subtle">on <span className="font-medium text-ink/80">{reader.book}</span></p>
      </div>
      <div className="ml-auto"><Stars value={reader.rating} size={12} /></div>
    </div>
    <p className="font-display text-[17px] leading-snug text-ink text-balance">“{reader.comment}”</p>
    <div className="mt-1 flex items-center gap-4 text-xs font-medium text-subtle">
      <span className="inline-flex items-center gap-1.5"><Heart className="h-3.5 w-3.5 text-coral" fill="currentColor" />{reader.likes}</span>
      <span className="inline-flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" />Reply</span>
    </div>
  </div>
);

/* ---------------- MascotContainer ---------------- */
export const MascotContainer = ({ src, alt, float = true, className = "", imgClassName = "" }) => (
  <div className={cx("relative select-none", className)}>
    <img src={src} alt={alt} loading="lazy" draggable="false"
      className={cx("h-full w-full object-contain drop-shadow-[0_30px_40px_rgba(52,45,41,0.18)]", float && "animate-float", imgClassName)} />
  </div>
);

/* ---------------- Magnetic (cursor-follow wrapper) ---------------- */
export function Magnetic({ children, strength = 0.35, className = "" }) {
  const ref = React.useRef(null);
  const [t, setT] = React.useState({ x: 0, y: 0 });
  const reduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const onMove = (e) => {
    if (reduce) return;
    const r = ref.current.getBoundingClientRect();
    setT({ x: (e.clientX - (r.left + r.width / 2)) * strength, y: (e.clientY - (r.top + r.height / 2)) * strength });
  };
  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={() => setT({ x: 0, y: 0 })}
      animate={{ x: t.x, y: t.y }} transition={{ type: "spring", stiffness: 220, damping: 16, mass: 0.4 }}
      className={cx("inline-block", className)}>
      {children}
    </motion.div>
  );
}

/* ---------------- TiltCard (subtle 3D tilt on hover) ---------------- */
export function TiltCard({ children, className = "", max = 8, ...props }) {
  const ref = React.useRef(null);
  const [tf, setTf] = React.useState("");
  const reduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTf(`perspective(900px) rotateX(${(-py * max).toFixed(2)}deg) rotateY(${(px * max).toFixed(2)}deg)`);
  };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => setTf("")}
      style={{ transform: tf, transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)", transformStyle: "preserve-3d" }}
      className={className} {...props}>
      {children}
    </div>
  );
}

export { cx };
